import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import KeyboardDoubleArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftOutlined';
import KeyboardDoubleArrowRightOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowRightOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';

import { useAuth } from '../store/AuthContext';
import logo from '../AzOtteMCRM.svg';

const menuSections = [
  {
    title: 'Workspace',
    items: [
      { label: 'Home', path: '/', icon: HomeOutlinedIcon, match: (p) => p === '/' },
      { label: 'Leads', path: '/leads', icon: PeopleAltOutlinedIcon, match: (p) => p === '/leads' || (p.startsWith('/leads/') && !p.startsWith('/leads/domain-check') && !p.startsWith('/leads/auto-enrichment') && !p.startsWith('/leads/contact-discovery') && !p.startsWith('/leads/auto-outreach')) },
      { label: 'Domain Check', path: '/leads/domain-check', icon: HealthAndSafetyOutlinedIcon, match: (p) => p.startsWith('/leads/domain-check') },
      { label: 'Auto Enrichment', path: '/leads/auto-enrichment', icon: AutoAwesomeOutlinedIcon, match: (p) => p.startsWith('/leads/auto-enrichment') },
      { label: 'Contact Discovery', path: '/leads/contact-discovery', icon: PersonSearchOutlinedIcon, match: (p) => p.startsWith('/leads/contact-discovery') },
      { label: 'Auto Outreach', path: '/leads/auto-outreach', icon: MailOutlineOutlinedIcon, match: (p) => p.startsWith('/leads/auto-outreach') },
      { label: 'Inbox', path: '/outreach/inbox', icon: MarkEmailReadOutlinedIcon, match: (p) => p.startsWith('/outreach/inbox') },
      { label: 'Email Queue', path: '/email-queue', icon: ForumOutlinedIcon, match: (p) => p === '/email-queue' },
      { label: 'Queue Monitor', path: '/email-queue/monitor', icon: InsightsOutlinedIcon, match: (p) => p.startsWith('/email-queue/monitor') },
      { label: 'Automation', path: '/automation', icon: SmartToyOutlinedIcon, match: (p) => p.startsWith('/automation') },
      { label: 'AI Search', path: '/ai-search', icon: SearchOutlinedIcon, match: (p) => p.startsWith('/ai-search') },
      { label: 'Customers', path: '/customers', icon: BusinessOutlinedIcon, match: (p) => p.startsWith('/customers') },
      { label: 'Contacts', path: '/contacts', icon: PersonSearchOutlinedIcon, match: (p) => p.startsWith('/contacts') },
      { label: 'Activities', path: '/activities', icon: TimelineOutlinedIcon, match: (p) => p.startsWith('/activities') },
      {
        label: 'Demo Bookings',
        path: '/management?tab=demo-bookings',
        icon: EventAvailableOutlinedIcon,
        match: (p, t) => p.startsWith('/management') && t === 'demo-bookings',
      },
    ],
  },
  {
    title: 'Sales Flow',
    items: [
      { label: 'Engage', path: '/engage', icon: ChatBubbleOutlineOutlinedIcon, match: (p) => p.startsWith('/engage') },
      { label: 'Email Queue', path: '/email-queue', icon: ScheduleOutlinedIcon, match: (p) => p === '/email-queue' },
      { label: 'Segmentation', path: '/segmentation', icon: RuleOutlinedIcon, match: (p) => p.startsWith('/segmentation') },
      { label: 'Calendar', path: '/calendar', icon: CalendarMonthOutlinedIcon, match: (p) => p.startsWith('/calendar') },
      { label: 'Profile', path: '/profile', icon: AccountCircleOutlinedIcon, match: (p) => p === '/profile' },
    ],
  },
];

const adminGroups = [
  {
    key: 'access',
    title: 'Access',
    icon: ShieldOutlinedIcon,
    items: [
      { label: 'Users', tab: 'users', icon: ManageAccountsOutlinedIcon },
    ],
  },
  {
    key: 'data',
    title: 'Data Model',
    icon: StorageOutlinedIcon,
    items: [
      { label: 'Industries', tab: 'industries', icon: FactoryOutlinedIcon },
      { label: 'Industry Groups', tab: 'industry-groups', icon: LayersOutlinedIcon },
      { label: 'Field Templates', tab: 'fields', icon: DashboardCustomizeOutlinedIcon },
      { label: 'Profiling', tab: 'profiling', icon: AssignmentOutlinedIcon },
    ],
  },
  {
    key: 'communication',
    title: 'Communication',
    icon: ForumOutlinedIcon,
    items: [
      { label: 'Interaction Templates', tab: 'interaction-templates', icon: ChatBubbleOutlineOutlinedIcon },
      { label: 'Email Templates', tab: 'email-templates', icon: MailOutlineOutlinedIcon },
      { label: 'Email Settings', tab: 'email-settings', icon: MarkEmailReadOutlinedIcon },
    ],
  },
  {
    key: 'scheduling',
    title: 'Scheduling',
    icon: ScheduleOutlinedIcon,
    items: [
      { label: 'Appointment Types', tab: 'appointment-types', icon: CalendarMonthOutlinedIcon },
    ],
  },
  {
    key: 'ai',
    title: 'AI & Marketing',
    icon: CampaignOutlinedIcon,
    items: [
      { label: 'Marketing Brain', tab: 'briefs', icon: PsychologyOutlinedIcon },
      { label: 'Signals', tab: 'signals', icon: SensorsOutlinedIcon },
      { label: 'AI Prompts', tab: 'prompts', icon: AutoAwesomeOutlinedIcon },
      { label: 'AI Providers', tab: 'ai', icon: SmartToyOutlinedIcon },
    ],
  },
  {
    key: 'system',
    title: 'System',
    icon: BugReportOutlinedIcon,
    items: [
      { label: 'Logs', tab: 'logs', icon: TerminalOutlinedIcon },
    ],
  },
];

const findActiveGroupKey = (tab, isOnManagement) => {
  if (!isOnManagement) return null;
  const group = adminGroups.find((g) => g.items.some((i) => i.tab === tab));
  return group ? group.key : null;
};

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const currentTab = searchParams.get('tab') || 'users';
  const isManagementActive = location.pathname.startsWith('/management');
  const isAdmin = user?.role === 'Admin';

  const dynamicSections = isAdmin
    ? [
        ...menuSections.map((s) => s.title === 'Sales Flow'
          ? {
            ...s,
            items: [
              ...s.items,
              { label: 'Analytics', path: '/analytics', icon: InsightsOutlinedIcon, match: (p) => p.startsWith('/analytics') },
            ],
          }
          : s),
        {
          title: 'Reports',
          items: [
            { label: 'Overview', path: '/reports/overview', icon: DashboardCustomizeOutlinedIcon, match: (p) => p === '/reports' || p.startsWith('/reports/overview') },
            { label: 'Leads', path: '/reports/leads', icon: TrendingUpOutlinedIcon, match: (p) => p.startsWith('/reports/leads') },
            { label: 'Industries', path: '/reports/industries', icon: FactoryOutlinedIcon, match: (p) => p.startsWith('/reports/industries') },
            { label: 'Outreach', path: '/reports/outreach', icon: ChatBubbleOutlineOutlinedIcon, match: (p) => p.startsWith('/reports/outreach') },
            { label: 'Segmentations', path: '/reports/segmentations', icon: RuleOutlinedIcon, match: (p) => p.startsWith('/reports/segmentations') },
            { label: 'AI Ops', path: '/reports/ai-ops', icon: SmartToyOutlinedIcon, match: (p) => p.startsWith('/reports/ai-ops') },
            { label: 'Marketing', path: '/reports/marketing', icon: InsightsOutlinedIcon, match: (p) => p.startsWith('/reports/marketing') },
          ],
        },
      ]
    : menuSections;

  const [openGroups, setOpenGroups] = useState(() => {
    const activeKey = findActiveGroupKey(currentTab, isManagementActive);
    return activeKey ? { [activeKey]: true } : {};
  });

  const nav = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleGroup = (key) => {
    if (collapsed) {
      setCollapsed(false);
      setOpenGroups((prev) => ({ ...prev, [key]: true }));
      return;
    }
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderLink = ({ label, path, icon: Icon, match }) => {
    const isActive = match(location.pathname, currentTab);
    return (
      <button
        key={path}
        className={`sidebar-link${isActive ? ' active' : ''}`}
        onClick={() => nav(path)}
        title={collapsed ? label : undefined}
      >
        <Icon className="sidebar-icon" fontSize="small" />
        {!collapsed && <span className="sidebar-link-text">{label}</span>}
      </button>
    );
  };

  return (
    <>
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}>
        <div className="sidebar-header">
          <img
            src={logo}
            alt="AZOTTE MCRM"
            className="sidebar-logo"
            onClick={() => nav('/')}
          />
          {!collapsed && (
            <span className="sidebar-title" onClick={() => nav('/')}>MCRM</span>
          )}
        </div>

        <nav className="sidebar-nav">
          {dynamicSections.map((section) => (
            <div className="sidebar-section" key={section.title}>
              {!collapsed && (
                <div className="sidebar-section-title">{section.title}</div>
              )}
              {section.items.map(renderLink)}
            </div>
          ))}

          <div className="sidebar-section">
            {!collapsed && (
              <div className="sidebar-section-title">Administration</div>
            )}

            {adminGroups.map((group) => {
              const GroupIcon = group.icon;
              const isOpen = !!openGroups[group.key];
              const groupActive =
                isManagementActive && group.items.some((i) => i.tab === currentTab);

              return (
                <div className="sidebar-group" key={group.key}>
                  <button
                    className={`sidebar-link${groupActive ? ' active' : ''}`}
                    onClick={() => toggleGroup(group.key)}
                    title={collapsed ? group.title : undefined}
                  >
                    <GroupIcon className="sidebar-icon" fontSize="small" />
                    {!collapsed && (
                      <>
                        <span className="sidebar-link-text">{group.title}</span>
                        <ExpandMoreOutlinedIcon
                          className={`sidebar-chevron${isOpen ? ' open' : ''}`}
                          fontSize="small"
                        />
                      </>
                    )}
                  </button>

                  {!collapsed && isOpen && (
                    <div className="sidebar-children">
                      {group.items.map(({ label, tab, icon: Icon }) => (
                        <button
                          key={tab}
                          className={`sidebar-link sidebar-child${
                            isManagementActive && currentTab === tab ? ' active' : ''
                          }`}
                          onClick={() => nav(`/management?tab=${tab}`)}
                        >
                          <Icon className="sidebar-icon" fontSize="small" />
                          <span className="sidebar-link-text">{label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        <div className="sidebar-footer">
          <div
            className="sidebar-user"
            onClick={() => nav('/my-account')}
            style={{ cursor: 'pointer' }}
            title="My Account"
          >
            <div className="sidebar-avatar">
              {(user?.fullName || user?.username || '?').charAt(0).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">{user?.fullName || user?.username}</span>
                {user?.role && <span className="badge badge-sidebar-role">{user.role}</span>}
              </div>
            )}
          </div>

          <button
            className={`sidebar-link${location.pathname === '/my-account' ? ' active' : ''}`}
            onClick={() => nav('/my-account')}
            title={collapsed ? 'My Account' : undefined}
          >
            <AccountCircleOutlinedIcon className="sidebar-icon" fontSize="small" />
            {!collapsed && <span className="sidebar-link-text">My Account</span>}
          </button>

          <button
            className="sidebar-link sidebar-logout"
            onClick={handleLogout}
            title={collapsed ? 'Logout' : undefined}
          >
            <LogoutOutlinedIcon className="sidebar-icon" fontSize="small" />
            {!collapsed && <span className="sidebar-link-text">Logout</span>}
          </button>
        </div>

        <button
          className="sidebar-collapse-btn"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {collapsed ? (
            <KeyboardDoubleArrowRightOutlinedIcon fontSize="small" />
          ) : (
            <KeyboardDoubleArrowLeftOutlinedIcon fontSize="small" />
          )}
        </button>
      </aside>
    </>
  );
}
